import { acquisitionForDraft } from "@/lib/acquisition";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Zap, Target, TrendingUp, Share2, Brain, Trophy } from "lucide-react";
import { Logo } from "@/components/Logo";
import { HowItWorks, Testimonials } from "@/components/Trust";
import { track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { WhyNxT } from "@/components/WhyNxT";
import { Link } from "react-router-dom";
import { ContactLinks } from "@/components/ContactLinks";

const stagger = { show: { transition: { staggerChildren: 0.1 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } } };

function DnaHeroCard() {
  const rows = [
    ["Analytical", 87], ["Creative", 74], ["Execution", 91], ["People", 68], ["Leadership", 79],
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: -2 }}
      animate={{ opacity: 1, y: 0, rotate: -2 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      className="animate-float w-full max-w-sm rounded-[28px] border border-white/10 bg-gradient-to-br from-indigo-600 via-violet-600 to-blue-700 p-6 text-white shadow-2xl shadow-indigo-900/40"
      data-testid="hero-dna-card"
    >
      <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">Your Career DNA</div>
      <div className="font-display text-3xl font-extrabold tracking-tight">The Strategist</div>
      <div className="mt-1 text-sm text-white/70">You turn complexity into direction.</div>
      <div className="mt-5 space-y-2.5">
        {rows.map(([label, val], i) => (
          <div key={label}>
            <div className="flex justify-between text-xs font-semibold">
              <span className="text-white/80">{label}</span>
              <span className="font-mono">{val}%</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/15">
              <motion.div className="h-full rounded-full bg-white"
                initial={{ width: 0 }} animate={{ width: `${val}%` }}
                transition={{ duration: 1, delay: 0.8 + i * 0.1 }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl bg-white/10 p-4 backdrop-blur">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Best Career Match</div>
        <div className="flex items-end justify-between">
          <div className="font-display text-xl font-bold">Product Manager</div>
          <div className="font-mono text-2xl font-extrabold text-amber-300">91%</div>
        </div>
      </div>
    </motion.div>
  );
}

const features = [
  { icon: Brain, k: "f1" }, { icon: Target, k: "f2" }, { icon: Share2, k: "f3" },
  { icon: Zap, k: "f4" }, { icon: TrendingUp, k: "f5" }, { icon: Trophy, k: "f6" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  useEffect(() => { acquisitionForDraft(); track("landing_view"); }, []);

  const start = () => {
    track("signup_started");
    navigate("/onboarding");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <LangToggle className="hidden sm:inline-flex" />
            <button onClick={() => navigate("/login")} data-testid="nav-login-btn"
              className="text-sm font-semibold text-muted-foreground hover:text-foreground">{t("login")}</button>
            <button onClick={start} data-testid="nav-cta-btn"
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-600">
              {t("getStarted")}
            </button>
          </div>
        </div>
        <ContactLinks testId="landing-header" className="mx-auto max-w-6xl px-5 pb-3" />
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 top-40 h-96 w-96 rounded-full bg-violet-400/10 blur-3xl" />
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-14 md:grid-cols-2 md:py-24">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.div variants={item} className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3.5 py-1.5 text-xs font-bold text-indigo-700">
              <Sparkles className="h-3.5 w-3.5" /> {t("tagline")}
            </motion.div>
            <motion.div variants={item} className="mb-4 sm:hidden"><LangToggle /></motion.div>
            <motion.h1 variants={item} className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
              {t("heroTitle1")}<br /><span className="text-indigo-600">{t("heroTitle2")}</span>
            </motion.h1>
            <motion.p variants={item} className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
              {t("heroSub")}
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={start} data-testid="hero-cta-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-7 py-4 text-base font-bold text-white shadow-lg shadow-indigo-600/30 transition-all hover:bg-indigo-700 hover:shadow-xl active:scale-[0.98]">
                {t("heroCta")}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#how" data-testid="hero-secondary-btn"
                className="inline-flex items-center justify-center rounded-full border border-border bg-card px-7 py-4 text-base font-bold transition-colors hover:bg-secondary">
                {t("heroSecondary")}
              </a>
            </motion.div>
            <motion.p variants={item} className="mt-5 text-sm text-muted-foreground">
              {t("heroNote")}
            </motion.p>
          </motion.div>
          <div className="flex justify-center md:justify-end">
            <DnaHeroCard />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="how" className="border-t border-border/60 bg-secondary/40 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-12 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">{t("howLabel")}</div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              {t("howTitle")}
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              {t("howSub")}
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div key={f.k}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group rounded-3xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600/10 text-indigo-600 transition-colors group-hover:bg-indigo-600 group-hover:text-white">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-bold">{t(`${f.k}t`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`${f.k}d`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20"><WhyNxT /></section>
      <section className="pb-4"><div className="mx-auto max-w-6xl px-5"><div className="mb-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">How NxT supports your next step</div><Testimonials /><div className="mt-3 text-center"><HowItWorks label="How is my Career DNA scored?" /></div></div></section>

      {/* Emotional journey / CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-5xl text-balance">
            {t("ctaTitle1")}<br />{t("ctaPre")}<span className="text-indigo-600">{t("ctaNext")}</span>{t("ctaPost")}
          </h2>
          <button onClick={start} data-testid="footer-cta-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-base font-bold text-white transition-all hover:bg-indigo-600 active:scale-[0.98]">
            {t("heroCta")} <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      <footer className="border-t border-border/60 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 text-center text-sm text-muted-foreground">
          <Logo />
          <p>{t("footerNote")}</p>
          <HowItWorks testId="footer-how-it-works-link" />
          <p className="text-xs">© {new Date().getFullYear()} NxT · CareerXAI · <Link to="/privacy" data-testid="footer-privacy" className="underline">Privacy</Link> · <Link to="/terms" data-testid="footer-terms" className="underline">Terms</Link></p>
        </div>
      </footer>
    </div>
  );
}
